import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Select,
  Form,
  Button,
  Flex,
  Spin,
  Input,
  ConfigProvider,
  DatePicker,
} from "antd";
import { LoadingOutlined, CloseOutlined } from "@ant-design/icons";
import { models, loadTypes, shippingTypes } from "./constants";
import { transportBodyTypes, transportCategories } from "./categories";
import GoogleMapsInput from "../orders/GoogleMapsInput";
import locale from "antd/locale/ru_RU";
import "dayjs/locale/ru";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import FloatingField from "../common/FloatingField";
import { fetchTrucks } from "../../utils/matchingApi";

const { RangePicker } = DatePicker;

const TransportSearchForm = ({
  setTransports,
  setTransportsInitial,
  allTransports,
  setAllTransports,
  suppressQueryAutoSubmit = false,
  setSuppressQueryAutoSubmit,
}) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const watchedValues = Form.useWatch([], form) || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [latLng, setLatLng] = useState({
    from: {},
    to: {},
  });

  const [city, setCity] = useState({
    from: "",
    to: "",
  });

  const [isInitialized, setIsInitialized] = useState(false);
  const [transportsLoaded, setTransportsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    initializeFromQueryParams();
    fetchInitial();
  }, []);

  // Filter transports when both initialization and transports loading are complete
  useEffect(() => {
    if (isInitialized && transportsLoaded && allTransports) {
      const params = Object.fromEntries(searchParams.entries());
      if (Object.keys(params).length > 0 && !suppressQueryAutoSubmit) {
        // Apply filters from query params
        filterTransports();
      }
    }
  }, [isInitialized, transportsLoaded, allTransports, suppressQueryAutoSubmit]);

  // Initialize form from query parameters
  const initializeFromQueryParams = () => {
    const params = Object.fromEntries(searchParams.entries());

    if (Object.keys(params).length > 0) {
      const formValues = {};

      // Handle simple string/number fields
      const stringFields = ["from_city", "to_city"];
      const numberFields = [
        "status",
        "radiusFrom",
        "radiusTo",
        "weight_from",
        "weight_to",
        "volume_from",
        "volume_to",
        "price_from",
        "price_to",
      ];

      stringFields.forEach((field) => {
        if (params[field]) {
          formValues[field] = params[field];
        }
      });

      numberFields.forEach((field) => {
        if (params[field]) {
          const numValue = Number(params[field]);
          if (!isNaN(numValue)) {
            formValues[field] = numValue;
          }
        }
      });

      // Handle array fields
      ["category", "body_type", "type_of_load"].forEach((field) => {
        if (params[field]) {
          try {
            const arrayValue = JSON.parse(params[field]);
            if (Array.isArray(arrayValue)) {
              formValues[field] = arrayValue;

              // Update selectedCategories if category is being restored
              if (field === "category") {
                setSelectedCategories(arrayValue.map((val) => +val));
              }
            }
          } catch (e) {
            console.warn(`Failed to parse ${field} from URL:`, e);
          }
        }
      });

      // Handle date range
      if (params.date_from && params.date_to) {
        formValues.dates = [dayjs(params.date_from), dayjs(params.date_to)];
      }

      // Handle location data
      if (params.lat_from && params.lng_from) {
        setLatLng((prev) => ({
          ...prev,
          from: {
            lat: parseFloat(params.lat_from),
            lng: parseFloat(params.lng_from),
          },
        }));
      }

      if (params.lat_to && params.lng_to) {
        setLatLng((prev) => ({
          ...prev,
          to: {
            lat: parseFloat(params.lat_to),
            lng: parseFloat(params.lng_to),
          },
        }));
      }

      if (params.city_from) {
        setCity((prev) => ({ ...prev, from: params.city_from }));
      }

      if (params.city_to) {
        setCity((prev) => ({ ...prev, to: params.city_to }));
      }

      // Set form values
      form.setFieldsValue(formValues);
    }

    setIsInitialized(true);
  };

  // Update query parameters when form changes
  const updateQueryParams = (values = form.getFieldsValue()) => {
    const params = new URLSearchParams();

    // Add simple fields
    const stringFields = ["from_city", "to_city"];
    const numberFields = [
      "status",
      "radiusFrom",
      "radiusTo",
      "weight_from",
      "weight_to",
      "volume_from",
      "volume_to",
      "price_from",
      "price_to",
    ];

    stringFields.forEach((field) => {
      if (values[field]) {
        params.set(field, values[field]);
      }
    });

    numberFields.forEach((field) => {
      if (values[field]) {
        params.set(field, values[field]);
      }
    });

    // Add array fields
    ["category", "body_type", "type_of_load"].forEach((field) => {
      if (
        values[field] &&
        Array.isArray(values[field]) &&
        values[field].length > 0
      ) {
        params.set(field, JSON.stringify(values[field]));
      }
    });

    // Add date range
    if (
      values.dates &&
      Array.isArray(values.dates) &&
      values.dates.length === 2
    ) {
      params.set("date_from", values.dates[0].format("YYYY-MM-DD"));
      params.set("date_to", values.dates[1].format("YYYY-MM-DD"));
    }

    // Add location data
    if (latLng.from.lat && latLng.from.lng) {
      params.set("lat_from", latLng.from.lat.toString());
      params.set("lng_from", latLng.from.lng.toString());
    }

    if (latLng.to.lat && latLng.to.lng) {
      params.set("lat_to", latLng.to.lat.toString());
      params.set("lng_to", latLng.to.lng.toString());
    }

    if (city.from) params.set("city_from", city.from);
    if (city.to) params.set("city_to", city.to);

    setSearchParams(params, { replace: true });
  };

  // Filter transports when city state changes
  useEffect(() => {
    if (isInitialized && allTransports && allTransports.length > 0) {
      if (window.innerWidth > 992) filterTransports();
    }
  }, [city, isInitialized]);

  // Filter transports when latLng state changes
  useEffect(() => {
    if (isInitialized && allTransports && allTransports.length > 0) {
      if (window.innerWidth > 992) filterTransports();
    }
  }, [latLng, isInitialized]);

  const fetchInitial = async () => {
    setLoading(true);
    try {
      const { status, data } = await axios({
        url: `${process.env.REACT_APP_API_URL}/transports/`,
        method: "GET",
      });
      const seen = new Set();
      const uniqueOwners = data.filter((item) => {
        if (seen.has(item.owner.id)) return false;
        seen.add(item.owner.id);
        return true;
      });

      // let filtered = uniqueOwners.filter((item) => item.owner.online && item.owner.location_from && item.owner.location_to);


      // Live availability from the matching service is the source of truth
      // for status/direction/freshness (legacy owner fields + ads are fallback)
      const liveByOwner = {};
      try {
        const live = await fetchTrucks();
        (live?.trucks || []).forEach((lt) => {
          liveByOwner[lt.driver_id] = lt;
        });
      } catch (e) {
        console.log("live availability unavailable:", e?.message);
      }
      const withLive = [...uniqueOwners].map((item) => {
        const live = item.owner ? liveByOwner[item.owner.id] : null;
        if (!live) return item;
        const destCity = (live.destinations && live.destinations[0]?.city) || null;
        const availDate = live.available_from
          ? String(live.available_from).slice(0, 10)
          : null;
        return {
          ...item,
          live,
          owner: {
            ...item.owner,
            online: live.status === "ACTIVE" ? 1 : 3,
            location_from: live.city,
            location_to: destCity || "Любое направление",
            from_latitude: live.lat,
            from_longitude: live.lng,
            from_date: availDate || item.owner?.from_date,
            to_date: availDate || item.owner?.to_date,
            weight: item.owner?.weight ?? live.max_load_weight,
            volume: item.owner?.volume ?? live.max_load_volume,
          },
        };
      });

      const now = new Date();
      const currentUTCDateStart = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );

      const all = withLive
        .filter((item) => {
          if (item?.owner?.online === 3) {
            return true;
          }

          const toDate =
            item.model_type === "TransportAd"
              ? item.to_date
              : item.owner?.to_date;

          if (toDate) {
            const itemToDate = new Date(toDate);
            // Convert to_date to end of day UTC for comparison
            const itemToDateEndOfDayUTC = new Date(
              Date.UTC(
                itemToDate.getUTCFullYear(),
                itemToDate.getUTCMonth(),
                itemToDate.getUTCDate(),
                23,
                59,
                59,
                999,
              ),
            );

            return itemToDateEndOfDayUTC >= currentUTCDateStart;
          }
          return true;
        })
        .sort((a, b) => {
          const la = a.live ? new Date(a.live.confirmed_at).getTime() : 0;
          const lb = b.live ? new Date(b.live.confirmed_at).getTime() : 0;
          return lb - la;
        });

      setTransports(all);
      setAllTransports(all);
      setTransportsLoaded(true);
    } catch (error) {
      console.log(error);
      setTransportsLoaded(true); // Set to true even on error to prevent hanging
    } finally {
      setLoading(false);
    }
  };



  const onFinishFailed = () => {
    console.log("failed");
    const el = document.querySelector(".middle-sidebar__wrapper");
    el.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const clearForm = () => {
    console.log('asdasd');
    form.resetFields();
    setCity({ from: "", to: "" });
    setLatLng({ from: {}, to: {} });
    setSelectedCategories([]);
    setSearchParams({}, { replace: true });
    setTransports(allTransports || []);
    setTransportsInitial(true);
  };

  useEffect(() => {
    window.addEventListener('clearTransports', clearForm);
    return () => window.removeEventListener('clearTransports', clearForm);
  });

  // Get data from Transport model (ads removed — fleet + drivers only)
  const getItemData = (item) => {
    return {
      weight: item.owner.weight,
      volume: item.owner.volume,
      price_from: item.owner.price_from,
      status: item.owner.status,
      location_from: item.owner.location_from,
      location_to: item.owner.location_to,
      from_latitude: item.owner.from_latitude,
      from_longitude: item.owner.from_longitude,
      to_latitude: item.owner.to_latitude,
      to_longitude: item.owner.to_longitude,
      category: item.category,
      body_type: item.body_type,
      type_of_load: item.type_of_load,
      from_date: item.owner.from_date,
      to_date: item.owner.to_date,
    };
  };

  // Filter function to filter allTransports client-side
  const filterTransports = () => {
    if (!allTransports || allTransports.length === 0 || !isInitialized) return;

    console.log("allTransports", allTransports);
    if (setSuppressQueryAutoSubmit) {
      setSuppressQueryAutoSubmit(false);
    }
    setLoading(true);
    const values = form.getFieldsValue();

    // Update query parameters
    updateQueryParams(values);

    // let filtered = allTransports.filter((item) => item.owner.online && item.owner.location_from && item.owner.location_to);
    let filtered = [...allTransports];

    if (values.type_of_load) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return Array.isArray(itemData.type_of_load)
          ? itemData.type_of_load.some((type) =>
            values.type_of_load.includes(+type),
          )
          : values.type_of_load.includes(+itemData.type_of_load);
      });
    }

    if (values.category && values.category.length > 0) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return values.category.includes(+itemData.category);
      });
    }

    if (values.body_type && values.body_type.length > 0) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return values.body_type.includes(+itemData.body_type);
      });
    }

    if (values.weight_from) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.weight && +itemData.weight >= +values.weight_from;
      });
    }
    if (values.weight_to) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.weight && +itemData.weight <= +values.weight_to;
      });
    }
    if (values.volume_from) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.volume && +itemData.volume >= +values.volume_from;
      });
    }
    if (values.volume_to) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.volume && +itemData.volume <= +values.volume_to;
      });
    }
    if (values.price_from) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return (
          itemData.price_from && +itemData.price_from >= +values.price_from
        );
      });
    }
    if (values.price_to) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.price_from && +itemData.price_from <= +values.price_to;
      });
    }
    if (values.status) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return itemData.status && +itemData.status === +values.status;
      });
    }

    // Date filtering - show ads available within the selected date range
    if (
      values.dates &&
      Array.isArray(values.dates) &&
      values.dates.length === 2
    ) {
      console.log("Date filtering with values:", values.dates);
      console.log("Items before date filter:", filtered.length);

      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);

        console.log("Item data:", {
          model_type: item.model_type,
          from_date: itemData.from_date,
          to_date: itemData.to_date,
        });

        if (!itemData.from_date || !itemData.to_date) {
          console.log("Rejected: missing date fields");
          return false;
        }

        const selectedFromDate = values.dates[0].toDate();
        const selectedToDate = values.dates[1].toDate();
        const itemFromDate = new Date(itemData.from_date);
        const itemToDate = new Date(itemData.to_date);

        console.log("Date comparison:", {
          selected: {
            from: selectedFromDate.toISOString(),
            to: selectedToDate.toISOString(),
          },
          item: {
            from: itemFromDate.toISOString(),
            to: itemToDate.toISOString(),
          },
          overlap_check: `${itemFromDate.toISOString()} <= ${selectedToDate.toISOString()} && ${itemToDate.toISOString()} >= ${selectedFromDate.toISOString()}`,
          result:
            itemFromDate <= selectedToDate && itemToDate >= selectedFromDate,
        });

        // Show ads that are available during the selected period
        // Item's availability period must overlap with the selected period
        const result =
          itemFromDate <= selectedToDate && itemToDate >= selectedFromDate;
        return result;
      });
    }

    if (values.radiusFrom && latLng.from.lat && latLng.from.lng) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        if (!itemData.from_latitude || !itemData.from_longitude) return false;

        const distance = calculateDistance(
          latLng.from.lat,
          latLng.from.lng,
          itemData.from_latitude,
          itemData.from_longitude,
        );
        return distance <= values.radiusFrom * 1000;
      });
    } else if (city.from) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        return (
          itemData.location_from &&
          (itemData.location_from.includes(city.from) ||
            itemData.location_from === city.from)
        );
      });
    }

    if (values.radiusTo && latLng.to.lat && latLng.to.lng) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        if (!itemData.to_latitude || !itemData.to_longitude) return false;

        const distance = calculateDistance(
          latLng.to.lat,
          latLng.to.lng,
          itemData.to_latitude,
          itemData.to_longitude,
        );

        return distance <= values.radiusTo * 1000;
      });
    } else if (city.to) {
      filtered = filtered.filter((item) => {
        const itemData = getItemData(item);
        // live "any direction" trucks match any destination filter
        return (
          itemData.location_to &&
          (itemData.location_to === "Любое направление" ||
            itemData.location_to.includes(city.to) ||
            itemData.location_to === city.to)
        );
      });
    }

    setTransportsInitial(false);
    setTransports(filtered);
    setLoading(false);
    const el = document.querySelector(".ant-list");
    if (el) {
      el.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Form field value change handler to trigger filtering
  const onFieldsChange = () => {
    if (isInitialized) {
      if (window.innerWidth > 992) filterTransports();
    }
  };

  // Handle form submission (using the same filterTransports logic)
  const onSubmit = () => {
    filterTransports();
  };

  const datePickerChange = (val) => {
    if (val && val.length === 2) {
      form.setFieldsValue({ dates: val });
      // Manually trigger filtering since setFieldsValue doesn't trigger onFieldsChange
      if (isInitialized) {
        if (window.innerWidth > 992) filterTransports();
      }
    }
  };

  const reverseLocations = () => {
    const { from, to } = city;
    setCity({
      from: to,
      to: from,
    });
    setLatLng({
      from: latLng.to,
      to: latLng.from,
    });
    form.setFieldsValue({
      radiusFrom: form.getFieldValue("radiusTo"),
      radiusTo: form.getFieldValue("radiusFrom"),
    });
    form.setFieldsValue({
      from_city: form.getFieldValue("to_city"),
      to_city: form.getFieldValue("from_city"),
    });
  };

  return (
    <div className="transport-search-form">
      {loading && !isMobile && (
        <div className="transport-search-full-screen">
          <Flex align="center" gap="middle">
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </Flex>
        </div>
      )}
      <Form
        form={form}
        name="basic"
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        onFieldsChange={onFieldsChange}
        autoComplete="off"
        layout="vertical"
      >
        <div className="transport-search-form__row transport-search-form__row--places">
          <div className="transport-search-form__places">
            <GoogleMapsInput
              required={false}
              form={form}
              setLatLng={setLatLng}
              setCity={setCity}
              direction="from"
              field="from_city"
              latLng={latLng}
              city={city}
              placeholder="Откуда"
              floatingLabel
            />
            <div className="transport-search-form__places__radius">
              <Form.Item
                name="radiusFrom"
                label=" "
                disabled={latLng.from.lat === 0 || latLng.from.lat === null}
              >
                <FloatingField label="Радиус" active={watchedValues.radiusFrom}>
                  <Input
                    placeholder=""
                    size="large"
                    disabled={!latLng.from.lat}
                  />
                </FloatingField>
              </Form.Item>
            </div>
          </div>
          <div
            className="transport-search-form__reverse"
            onClick={reverseLocations}
          />
          <div className="transport-search-form__places">
            <GoogleMapsInput
              required={false}
              form={form}
              setLatLng={setLatLng}
              setCity={setCity}
              direction="to"
              field="to_city"
              latLng={latLng}
              city={city}
              placeholder="Куда"
              floatingLabel
            />
            <div className="transport-search-form__places__radius">
              <Form.Item name="radiusTo" label=" ">
                <FloatingField label="Радиус" active={watchedValues.radiusTo}>
                  <Input
                    placeholder=""
                    size="large"
                    disabled={!latLng.to.lat}
                  />
                </FloatingField>
              </Form.Item>
            </div>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="dates" label=" ">
              <ConfigProvider locale={locale}>
                <FloatingField label="Дата погрузки" active={watchedValues.dates}>
                  <RangePicker
                    placeholder={["", ""]}
                    onChange={datePickerChange}
                    locale={locale}
                    size="large"
                    inputReadOnly={isMobile}
                    placement={isMobile ? "bottomLeft" : undefined}
                    popupClassName={
                      isMobile ? "mobile-range-picker-dropdown" : undefined
                    }
                    value={form.getFieldValue("dates")}
                  />
                </FloatingField>
              </ConfigProvider>
            </Form.Item>
          </div>
          <div className="transport-search-form__divider desktop-only" />
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="status" label=" ">
              <FloatingField
                label="Тип перевозки"
                active={watchedValues.status !== undefined}
              >
                <Select
                  placeholder=""
                  options={[{ value: "", label: "Все" }, ...shippingTypes]}
                  size="large"
                />
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="category" label=" ">
              <FloatingField label="Категория авто" active={watchedValues.category}>
                <Select
                  placeholder=""
                  size="large"
                  mode="multiple"
                  onChange={(values) => {
                    // Convert values to numbers since category IDs in the data are numbers
                    const numericValues = values.map((val) => +val);
                    setSelectedCategories(numericValues);

                    // Get currently selected body types
                    const currentBodyTypes =
                      form.getFieldValue("transport_body_type") || [];

                    if (
                      currentBodyTypes.length === 0 ||
                      numericValues.length === 0
                    ) {
                      return; // Nothing to filter
                    }

                    // Find valid body types based on the new categories
                    const validBodyTypeIds = [];

                    // Get parent types that match the selected categories
                    const validParentTypes = transportBodyTypes
                      .filter((item) => item.parent_id === null)
                      .filter((parent) =>
                        parent.category.some((cat) =>
                          numericValues.includes(+cat),
                        ),
                      );

                    // Get all valid child IDs
                    validParentTypes.forEach((parent) => {
                      const children = transportBodyTypes.filter(
                        (item) => item.parent_id === parent.id,
                      );
                      children.forEach((child) =>
                        validBodyTypeIds.push(child.id),
                      );
                    });

                    // Filter out invalid selections
                    const filteredBodyTypes = currentBodyTypes.filter((id) =>
                      validBodyTypeIds.includes(+id),
                    );

                    // Update form if needed
                    if (filteredBodyTypes.length !== currentBodyTypes.length) {
                      console.log(
                        "Removing invalid body types",
                        currentBodyTypes,
                        "->",
                        filteredBodyTypes,
                      );
                      form.setFieldsValue({
                        transport_body_type:
                          filteredBodyTypes.length > 0
                            ? filteredBodyTypes
                            : undefined,
                      });
                    }
                  }}
                >
                  {transportCategories.map((category) => (
                    <Select.Option
                      key={category.value}
                      value={category.value}
                      className={`transport_category ${category.className}`}
                    >
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              </FloatingField>
            </Form.Item>
          </div>
          <div className="transport-search-form__divider desktop-only" />
          <div className="transport-search-form__row transport-search-form__row--child-full">
            <Form.Item name="body_type" label=" ">
              <FloatingField label="Тип кузова" active={watchedValues.body_type}>
                <Select
                  placeholder=""
                  size="large"
                  mode="multiple"
                  placement={isMobile ? "topLeft" : "bottomLeft"}
                >
                  {useMemo(() => {
                    // Find all parent body types (with parent_id: null)
                    const parentBodyTypes = transportBodyTypes.filter(
                      (item) => item.parent_id === null,
                    );

                    // Filter parent body types based on selected categories if any
                    const filteredParentTypes =
                      selectedCategories.length > 0
                        ? parentBodyTypes.filter((parent) => {
                          // Check if this parent's category array includes any of the selected categories
                          return parent.category.some((cat) =>
                            selectedCategories.includes(+cat),
                          );
                        })
                        : parentBodyTypes;

                    return filteredParentTypes.map((parent) => (
                      <Select.OptGroup key={parent.id} label={parent.name}>
                        {transportBodyTypes
                          .filter((child) => child.parent_id === parent.id)
                          .map((child) => (
                            <Select.Option key={child.id} value={child.id}>
                              {child.name}
                            </Select.Option>
                          ))}
                      </Select.OptGroup>
                    ));
                  }, [selectedCategories])}
                </Select>
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <div className="transport-search-form__row transport-search-form__row--stack-mobile">
          <div className="transport-search-form__row two-way transport-search-form-weight">
            <Form.Item name="weight_from" label=" ">
              <FloatingField label="Вес от" active={watchedValues.weight_from}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
            <Form.Item name="weight_to" label=" ">
              <FloatingField label="до (тонны)" active={watchedValues.weight_to}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
          </div>
          <div className="transport-search-form__row two-way">
            <Form.Item name="volume_from" label=" ">
              <FloatingField label="Объём от" active={watchedValues.volume_from}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
            <Form.Item name="volume_to" label=" ">
              <FloatingField label="до (м3)" active={watchedValues.volume_to}>
                <Input className="suffix-input" prefix="" type="number" size="large" />
              </FloatingField>
            </Form.Item>
          </div>
        </div>
        <Flex justify="space-between" className="transport-search-bottom-buttons">
          <Button
            type="primary"
            className="mobile-only transport-search-submit"
            onClick={() => form.submit()}
          >
            Применить
          </Button>
          <div className="transport-search-form__clear" onClick={clearForm}>
            <CloseOutlined style={{ fontSize: 16 }} />
            Сбросить
          </div>
        </Flex>
      </Form>
    </div>
  );
};

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
    Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) *
    Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance; // Distance in meters
};

// Convert degrees to radians
const toRad = (value) => {
  return (value * Math.PI) / 180;
};

export default TransportSearchForm;
